import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from '../../utils/toast';

const CustomPageForm = ({ page, onSubmit, onCancel }) => {
  const { checkPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [verificationTypes, setVerificationTypes] = useState([]);
  const [documentVerifications, setDocumentVerifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [documentSearchTerm, setDocumentSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [showDocumentDropdown, setShowDocumentDropdown] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    menuName: '',
    menuCategory: '',
    description: '',
    fields: [],
    verificationTypes: [],
    documentVerifications: [],
    accessUsers: [],
    accessGroups: [],
    formSubmissionAction: 'save',
    autoFillField: '',
    customStatuses: ['draft', 'submitted', 'approved', 'rejected', 'completed'],
    expiredStatus: '',
    // New Status Settings
    allowStatusSelectionOnCreate: true,
    statusLockOnEdit: false,
    defaultStatus: 'submitted',
    // Document Inventory Settings
    allowDocumentInventory: false,
    documentInventorySettings: {
      allowedFileTypes: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png'],
      maxFileSize: 10,
      requireDocumentDate: true
    },
    allowExpensesTab: false,
    expensesSettings: {
      allowedFileTypes: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png'],
      maxFileSize: 10
    },
    // Public URL Settings
    allowPublicUrl: false,
    publicUrlToken: undefined, // Will be generated when allowPublicUrl is enabled
    publicUrlSettings: {
      enableCaptcha: false,
      allowPublicSubmissionAtGroupLevel: false,
      successMessage: 'Your submission has been received successfully!',
      customTitle: '',
      customDescription: ''
    },
    invoiceSettings: {
      enabled: false,
      fixedValue: 0,
      description: '',
      formTokens: []
    },
    taskSettings: {
      taskNameField: '',
      taskDescriptionField: '',
      deadlineField: '',
      defaultDeadline: {
        days: 0,
        hours: 0
      }
    },
    documentGenerationSettings: [],
    // Table Display Settings
    showColumnFilters: true
  });

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'dropdown', label: 'Dropdown' },
    { value: 'multitext', label: 'Multi-line Text' },
    { value: 'upload', label: 'File Upload' },
    { value: 'masterdata', label: 'Master Data Selection' },
    { value: 'user', label: 'User Selection' }
  ];

  useEffect(() => {
    fetchVerificationTypes();
    fetchDocumentVerifications();
    fetchUsers();
    fetchGroups();
    if (page) {
      // Normalize accessUsers and accessGroups to arrays of IDs
      // They might be populated objects from the backend
      const normalizeToIds = (items) => {
        if (!items || !Array.isArray(items)) return [];
        return items.map(item => {
          // If it's already an ID (string), return it
          if (typeof item === 'string') return item;
          // If it's an object with _id, return the _id
          if (item && typeof item === 'object' && item._id) return item._id;
          // Otherwise return the item as-is
          return item;
        });
      };

      // Normalize fields to ensure acceptedFileTypes is always an array
      const normalizedFields = (page.fields || []).map(field => ({
        ...field,
        acceptedFileTypes: normalizeAcceptedFileTypes(field.acceptedFileTypes)
      }));
      
      const docGenSettings = page.documentGenerationSettings || [];
      console.log('Loading custom page for editing. Document generation settings:', docGenSettings);
      
      setFormData({
        name: page.name || '',
        menuName: page.menuName || '',
        menuCategory: page.menuCategory || '',
        description: page.description || '',
        fields: normalizedFields,
        verificationTypes: page.verificationTypes ? normalizeToIds(page.verificationTypes) : [],
        documentVerifications: page.documentVerifications ? normalizeToIds(page.documentVerifications) : [],
        accessUsers: normalizeToIds(page.accessUsers || []),
        accessGroups: normalizeToIds(page.accessGroups || []),
        formSubmissionAction: page.formSubmissionAction || 'save',
        autoFillField: page.autoFillField || '',
        customStatuses: page.customStatuses || ['draft', 'submitted', 'approved', 'rejected', 'completed'],
        expiredStatus: page.expiredStatus || '',
        // New Status Settings
        allowStatusSelectionOnCreate: page.allowStatusSelectionOnCreate !== undefined ? page.allowStatusSelectionOnCreate : true,
        statusLockOnEdit: page.statusLockOnEdit || false,
        defaultStatus: page.defaultStatus || 'submitted',
        // Document Inventory Settings
        allowDocumentInventory: page.allowDocumentInventory || false,
        documentInventorySettings: page.documentInventorySettings || {
          allowedFileTypes: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png'],
          maxFileSize: 10,
          requireDocumentDate: true
        },
        allowExpensesTab: page.allowExpensesTab || false,
        expensesSettings: page.expensesSettings || {
          allowedFileTypes: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png'],
          maxFileSize: 10
        },
        // Table Display Settings
        showColumnFilters: page.showColumnFilters !== undefined ? page.showColumnFilters : true,
        // Public URL Settings
        allowPublicUrl: page.allowPublicUrl || false,
        publicUrlToken: page.publicUrlToken || undefined, // Include existing token if available
        publicUrlSettings: page.publicUrlSettings || {
          enableCaptcha: false,
          allowPublicSubmissionAtGroupLevel: false,
          successMessage: 'Your submission has been received successfully!',
          customTitle: '',
          customDescription: ''
        },
        invoiceSettings: page.invoiceSettings || {
          enabled: false,
          fixedValue: 0,
          description: '',
          formTokens: []
        },
        taskSettings: page.taskSettings || {
          taskNameField: '',
          taskDescriptionField: '',
          deadlineField: '',
          defaultDeadline: {
            days: 0,
            hours: 0
          }
        },
        documentGenerationSettings: docGenSettings
      });
    }
  }, [page]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-dropdown') && !event.target.closest('.group-dropdown') && !event.target.closest('.document-dropdown')) {
        setShowUserDropdown(false);
        setShowGroupDropdown(false);
        setShowDocumentDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchVerificationTypes = async () => {
    try {
      const response = await api.get('/verification-types');
      if (response.data.success) {
        setVerificationTypes(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching verification types:', error);
    }
  };

  const fetchDocumentVerifications = async () => {
    try {
      const response = await api.get('/custom-documents');
      if (response.data.success) {
        console.log("Checklist", response.data.data.docs);
        setDocumentVerifications(response.data.data.docs || response.data.data);
      }
    } catch (error) {
      console.error('Error fetching Checklist:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.get('/groups');
      console.log('Groups API response:', response.data);
      setGroups(response.data);
      console.log('Groups state set:', response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  // Generate a temporary token for preview (64-character hex string, matching backend format)
  const generateTempToken = () => {
    const chars = '0123456789abcdef';
    let token = '';
    for (let i = 0; i < 64; i++) {
      token += chars[Math.floor(Math.random() * chars.length)];
    }
    return token;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // If enabling allowPublicUrl and no token exists, generate a temporary one for preview
      if (field === 'allowPublicUrl' && value === true && !prev.publicUrlToken && !page?.publicUrlToken) {
        updated.publicUrlToken = generateTempToken();
      }
      
      // If disabling allowPublicUrl, clear the temporary token
      if (field === 'allowPublicUrl' && value === false && !page?.publicUrlToken) {
        updated.publicUrlToken = undefined;
      }
      
      // If enabling allowDocumentInventory, ensure documentInventorySettings is initialized
      if (field === 'allowDocumentInventory' && value === true && !prev.documentInventorySettings) {
        updated.documentInventorySettings = {
          allowedFileTypes: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png'],
          maxFileSize: 10,
          requireDocumentDate: true
        };
      }

      if (field === 'allowExpensesTab' && value === true && !prev.expensesSettings) {
        updated.expensesSettings = {
          allowedFileTypes: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png'],
          maxFileSize: 10
        };
      }
      
      return updated;
    });
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleFieldChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => 
        i === index ? { ...f, [field]: value } : f
      )
    }));
  };

  const addField = () => {
    const newField = {
      name: '',
      label: '',
      type: 'text',
      required: false,
      options: [],
      placeholder: '',
      validation: {},
      isDeadlineField: false,
      showInTable: true,
      showFilter: true,
      autoFill: false,
      consignmentTracking: false,
      acceptedFileTypes: [], // For file upload fields - always an array
      maxFileSize: 10, // Default 10MB
      order: formData.fields.length + 1
    };

    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };
  
  // Helper to normalize acceptedFileTypes to array
  const normalizeAcceptedFileTypes = (types) => {
    if (!types) return [];
    if (Array.isArray(types)) return types;
    if (typeof types === 'string') {
      return types.split(',').map(t => t.trim()).filter(t => t);
    }
    return [];
  };

  const removeField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const addOption = (fieldIndex) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((field, i) => 
        i === fieldIndex 
          ? { ...field, options: [...field.options, ''] }
          : field
      )
    }));
  };

  const removeOption = (fieldIndex, optionIndex) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((field, i) => 
        i === fieldIndex 
          ? { ...field, options: field.options.filter((_, j) => j !== optionIndex) }
          : field
      )
    }));
  };

  const handleOptionChange = (fieldIndex, optionIndex, value) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((field, i) => 
        i === fieldIndex 
          ? { 
              ...field, 
              options: field.options.map((opt, j) => 
                j === optionIndex ? value : opt
              )
            }
          : field
      )
    }));
  };

  const filteredUsers = users.filter(user => 
    !formData.accessUsers.includes(user._id) &&
    (user.firstName?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
     user.lastName?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
     user.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
     user.username?.toLowerCase().includes(userSearchTerm.toLowerCase()))
  );

  const filteredGroups = groups.filter(group => 
    !formData.accessGroups.includes(group._id) &&
    group.name?.toLowerCase().includes(groupSearchTerm.toLowerCase())
  );

  const filteredDocuments = documentVerifications.filter(doc => 
    !formData.documentVerifications.includes(doc._id) &&
    doc.name?.toLowerCase().includes(documentSearchTerm.toLowerCase())
  );

  const handleAddUser = (userId) => {
    if (!formData.accessUsers.includes(userId)) {
      setFormData(prev => ({
        ...prev,
        accessUsers: [...prev.accessUsers, userId]
      }));
    }
    setUserSearchTerm('');
    setShowUserDropdown(false);
  };

  const handleAddGroup = (groupId) => {
    if (!formData.accessGroups.includes(groupId)) {
      setFormData(prev => ({
        ...prev,
        accessGroups: [...prev.accessGroups, groupId]
      }));
    }
    setGroupSearchTerm('');
    setShowGroupDropdown(false);
  };

  const handleRemoveUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      accessUsers: prev.accessUsers.filter(id => id !== userId)
    }));
  };

  const handleRemoveGroup = (groupId) => {
    setFormData(prev => ({
      ...prev,
      accessGroups: prev.accessGroups.filter(id => id !== groupId)
    }));
  };

  const handleClearAllUsers = () => {
    setFormData(prev => ({
      ...prev,
      accessUsers: []
    }));
  };

  const handleClearAllGroups = () => {
    setFormData(prev => ({
      ...prev,
      accessGroups: []
    }));
  };

  const handleAddDocument = (documentId) => {
    console.log("Handle add document", documentId);
    if (!formData.documentVerifications.includes(documentId)) {
      setFormData(prev => ({
        ...prev,
        documentVerifications: [...prev.documentVerifications, documentId]
      }));
    }
    setDocumentSearchTerm('');
    setShowDocumentDropdown(false);
  };

  const handleRemoveDocument = (documentId) => {
    setFormData(prev => ({
      ...prev,
      documentVerifications: prev.documentVerifications.filter(id => id !== documentId)
    }));
  };

  const handleClearAllDocuments = () => {
    setFormData(prev => ({
      ...prev,
      documentVerifications: []
    }));
  };

  const handleAddStatus = () => {
    setFormData(prev => ({
      ...prev,
      customStatuses: [...prev.customStatuses, '']
    }));
  };

  const handleRemoveStatus = (index) => {
    setFormData(prev => {
      const newStatuses = prev.customStatuses.filter((_, i) => i !== index);
      // If removed status was the expired status, clear it
      const removedStatus = prev.customStatuses[index];
      const newExpiredStatus = prev.expiredStatus === removedStatus ? '' : prev.expiredStatus;
      return {
        ...prev,
        customStatuses: newStatuses,
        expiredStatus: newExpiredStatus
      };
    });
  };

  const handleStatusChange = (index, value) => {
    setFormData(prev => {
      const oldStatus = prev.customStatuses[index];
      const newStatuses = prev.customStatuses.map((status, i) => 
        i === index ? value : status
      );
      // Update expired status if the old status was selected as expired
      const newExpiredStatus = prev.expiredStatus === oldStatus ? value : prev.expiredStatus;
      return {
        ...prev,
        customStatuses: newStatuses,
        expiredStatus: newExpiredStatus
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.menuName || !formData.menuCategory) {
      toast.error('Name, menu name, and menu category are required');
      return;
    }

    if (formData.fields.length === 0) {
      toast.error('At least one field is required');
      return;
    }

    // Validate field names are unique
    const fieldNames = formData.fields.map(f => f.name);
    const uniqueFieldNames = new Set(fieldNames);
    if (fieldNames.length !== uniqueFieldNames.size) {
      toast.error('Field names must be unique');
      return;
    }

    // Validate required fields have names and labels
    for (const field of formData.fields) {
      if (!field.name || !field.label) {
        toast.error('All fields must have a name and label');
        return;
      }
    }

    // Validate custom statuses
    const validStatuses = formData.customStatuses.filter(s => s.trim() !== '');
    if (validStatuses.length === 0) {
      toast.error('At least one custom status is required');
      return;
    }
    
    // Check for duplicate statuses
    const uniqueStatuses = new Set(validStatuses);
    if (validStatuses.length !== uniqueStatuses.size) {
      toast.error('Status names must be unique');
      return;
    }

    // Validate expired status is in the status list
    if (formData.expiredStatus && !validStatuses.includes(formData.expiredStatus)) {
      toast.error('Expired status must be one of the custom statuses');
      return;
    }

    // Validate deadline field exists if task creation is enabled
    if (formData.formSubmissionAction === 'create_task' && formData.taskSettings.deadlineField) {
      const deadlineFieldExists = formData.fields.some(f => f.name === formData.taskSettings.deadlineField);
      if (!deadlineFieldExists) {
        toast.error('Deadline field must exist in the form fields');
        return;
      }
    }

    setLoading(true);
    try {
      // Filter out incomplete document generation settings
      // Only include settings that have both documentName and templatePath
      const totalDocSettings = (formData.documentGenerationSettings || []).length;
      console.log('Total document generation settings:', totalDocSettings);
      console.log('All settings:', formData.documentGenerationSettings);
      
      const completeDocSettings = (formData.documentGenerationSettings || []).filter((doc, idx) => {
        const isComplete = doc.documentName && doc.documentName.trim() !== '' && 
                          doc.templatePath && doc.templatePath.trim() !== '';
        console.log(`Document ${idx + 1}:`, {
          name: doc.documentName || '(empty)',
          hasTemplate: !!doc.templatePath,
          isComplete: isComplete
        });
        return isComplete;
      });

      console.log('Complete document settings count:', completeDocSettings.length);
      console.log('Complete settings:', completeDocSettings);

      // Warn user if some documents will be filtered out
      const incompleteCount = totalDocSettings - completeDocSettings.length;
      if (incompleteCount > 0) {
        toast.warning(`${incompleteCount} incomplete document setting(s) will not be saved. Please fill in both Document Name and Template File.`);
      }

      // Validate document generation settings
      for (const doc of completeDocSettings) {
        if (doc.generationCondition.type === 'on_status_change' && !doc.generationCondition.statusValue) {
          toast.error(`Document "${doc.documentName}" requires a status value to be selected`);
          setLoading(false);
          return;
        }
        if (doc.generationCondition.type === 'on_field_change' && !doc.generationCondition.fieldName) {
          toast.error(`Document "${doc.documentName}" requires a field name to be selected`);
          setLoading(false);
          return;
        }
      }

      // Ensure documentInventorySettings is included if allowDocumentInventory is true
      if (formData.allowDocumentInventory && !formData.documentInventorySettings) {
        formData.documentInventorySettings = {
          allowedFileTypes: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png'],
          maxFileSize: 10,
          requireDocumentDate: true
        };
      }

      if (formData.allowExpensesTab && !formData.expensesSettings) {
        formData.expensesSettings = {
          allowedFileTypes: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png'],
          maxFileSize: 10
        };
      }

      // Filter out empty statuses before submitting
      const submitData = {
        ...formData,
        customStatuses: formData.customStatuses.filter(s => s.trim() !== ''),
        documentGenerationSettings: completeDocSettings
      };

      console.log('=== SUBMITTING CUSTOM PAGE ===');
      console.log('allowDocumentInventory:', submitData.allowDocumentInventory);
      console.log('documentInventorySettings:', submitData.documentInventorySettings);
      console.log('allowPublicUrl:', submitData.allowPublicUrl);
      console.log('publicUrlSettings:', submitData.publicUrlSettings);
      console.log('Full submit data:', JSON.stringify(submitData, null, 2));
      
      await onSubmit(submitData);
      
      // Show success message with document count
      if (completeDocSettings.length > 0) {
        toast.success(`Custom page saved with ${completeDocSettings.length} document generation setting(s)`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">
            {page ? 'Edit Custom Page' : 'Create Custom Page'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Page Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter page name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Menu Name *
              </label>
              <input
                type="text"
                value={formData.menuName}
                onChange={(e) => handleInputChange('menuName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter menu name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Menu Category *
              </label>
              <input
                type="text"
                value={formData.menuCategory}
                onChange={(e) => handleInputChange('menuCategory', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter menu category"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter description"
              />
            </div>
          </div>

          {/* Form Submission Action */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Form Submission Action
            </label>
            <select
              value={formData.formSubmissionAction}
              onChange={(e) => handleInputChange('formSubmissionAction', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="save">Save Only</option>
              <option value="create_task">Create Task</option>
              <option value="create_invoice">Create Invoice</option>
            </select>
          </div>

          {/* Status Settings */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-medium mb-4">Status Settings</h3>
            
            <div className="space-y-4">
              {/* Allow Status Selection on Create */}
              <div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.allowStatusSelectionOnCreate !== undefined ? formData.allowStatusSelectionOnCreate : true}
                    onChange={(e) => handleInputChange('allowStatusSelectionOnCreate', e.target.checked)}
                    className="mr-2"
                    id="allowStatusSelection"
                  />
                  <label htmlFor="allowStatusSelection" className="text-sm font-medium text-gray-700">
                    Allow Status Selection on Record Creation
                  </label>
                </div>
                <p className="text-xs text-gray-500 ml-6 mt-1">
                  When enabled, users can select a status when creating a new record. When disabled, the default status is automatically applied.
                </p>
              </div>

              {/* Status Lock on Edit */}
              <div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.statusLockOnEdit || false}
                    onChange={(e) => handleInputChange('statusLockOnEdit', e.target.checked)}
                    className="mr-2"
                    id="statusLockOnEdit"
                  />
                  <label htmlFor="statusLockOnEdit" className="text-sm font-medium text-gray-700">
                    Lock Status After Creation
                  </label>
                </div>
                <p className="text-xs text-gray-500 ml-6 mt-1">
                  When enabled, the status cannot be changed after a record is created.
                </p>
              </div>

              {/* Default Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Status *
                </label>
                <select
                  value={formData.defaultStatus || 'submitted'}
                  onChange={(e) => handleInputChange('defaultStatus', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {formData.customStatuses.filter(s => s.trim() !== '').map((status, index) => (
                    <option key={index} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  This status will be automatically applied to new records when status selection is disabled or not provided.
                </p>
              </div>
            </div>
          </div>

          {/* Custom Status List */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Custom Status List</h3>
              <button
                type="button"
                onClick={handleAddStatus}
                className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 transition-colors text-sm"
              >
                Add Status
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Define custom statuses for submissions. At least one status is required.
            </p>
            <div className="space-y-2">
              {formData.customStatuses.map((status, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={status}
                    onChange={(e) => handleStatusChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter status name (e.g., draft, submitted)"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveStatus(index)}
                    disabled={formData.customStatuses.length === 1}
                    className="text-red-600 hover:text-red-800 px-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            
            {/* Expired Status Selection */}
            {formData.customStatuses.length > 0 && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expired Status
                </label>
                <select
                  value={formData.expiredStatus}
                  onChange={(e) => handleInputChange('expiredStatus', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">None - No expired status</option>
                  {formData.customStatuses.filter(s => s.trim() !== '').map((status, index) => (
                    <option key={index} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select which status should be considered as "expired" for this custom page.
                </p>
              </div>
            )}
          </div>

          {/* Auto-Fill Field Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Auto-Fill Field (Optional)
            </label>
            <select
              value={formData.autoFillField}
              onChange={(e) => handleInputChange('autoFillField', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">None - No auto-fill</option>
              {formData.fields
                .filter(field => field.name && field.label && ['text', 'email', 'number'].includes(field.type))
                .map((field, index) => (
                  <option key={index} value={field.name}>
                    {field.label} ({field.name})
                  </option>
                ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Select a field to enable auto-fill. When users enter a value in this field, they'll see suggestions from previous submissions with the same value.
            </p>
          </div>

          {/* Document Inventory Settings */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-medium mb-4">Document Inventory</h3>
            
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={formData.allowDocumentInventory || false}
                onChange={(e) => handleInputChange('allowDocumentInventory', e.target.checked)}
                className="mr-2"
                id="allowDocumentInventory"
              />
              <label htmlFor="allowDocumentInventory" className="text-sm font-medium text-gray-700">
                Enable Document Inventory (Upload Tab)
              </label>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              When enabled, each record will have its own Upload tab where users can upload multiple documents with document dates.
            </p>

            {formData.allowDocumentInventory && (
              <div className="space-y-4 bg-gray-50 p-4 rounded-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allowed File Types (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={(formData.documentInventorySettings?.allowedFileTypes || []).join(', ')}
                    onChange={(e) => {
                      const types = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                      handleNestedChange('documentInventorySettings', 'allowedFileTypes', types);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder=".pdf, .doc, .docx, .jpg, .png"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter file extensions separated by commas (e.g., .pdf, .doc, .docx)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum File Size (MB)
                  </label>
                  <input
                    type="number"
                    value={formData.documentInventorySettings?.maxFileSize || 10}
                    onChange={(e) => handleNestedChange('documentInventorySettings', 'maxFileSize', parseInt(e.target.value) || 10)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="100"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.documentInventorySettings?.requireDocumentDate !== false}
                    onChange={(e) => handleNestedChange('documentInventorySettings', 'requireDocumentDate', e.target.checked)}
                    className="mr-2"
                    id="requireDocumentDate"
                  />
                  <label htmlFor="requireDocumentDate" className="text-sm font-medium text-gray-700">
                    Require Document Date for Uploads
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Expenses Tab Settings */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-medium mb-4">Expenses Tab</h3>

            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={formData.allowExpensesTab || false}
                onChange={(e) => handleInputChange('allowExpensesTab', e.target.checked)}
                className="mr-2"
                id="allowExpensesTab"
              />
              <label htmlFor="allowExpensesTab" className="text-sm font-medium text-gray-700">
                Enable Expenses Tab
              </label>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              When enabled, each record will have an Expenses tab where users can add one or more line items with description, cost, and file uploads.
            </p>

            {formData.allowExpensesTab && (
              <div className="space-y-4 bg-gray-50 p-4 rounded-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allowed File Types (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={(formData.expensesSettings?.allowedFileTypes || []).join(', ')}
                    onChange={(e) => {
                      const types = e.target.value.split(',').map(t => t.trim()).filter(Boolean);
                      handleNestedChange('expensesSettings', 'allowedFileTypes', types);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder=".pdf, .jpg, .png"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max File Size (MB)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.expensesSettings?.maxFileSize || 10}
                    onChange={(e) => handleNestedChange('expensesSettings', 'maxFileSize', parseInt(e.target.value, 10) || 10)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Table Display Settings */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-medium mb-4">Table Display Settings</h3>
            
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={formData.showColumnFilters !== undefined ? formData.showColumnFilters : true}
                onChange={(e) => handleInputChange('showColumnFilters', e.target.checked)}
                className="mr-2"
                id="showColumnFilters"
              />
              <label htmlFor="showColumnFilters" className="text-sm font-medium text-gray-700">
                Show Column Filter Buttons
              </label>
            </div>
            <p className="text-xs text-gray-500">
              When enabled, filter buttons will be displayed in the table column headers, allowing users to filter submissions by column values.
            </p>
          </div>

          {/* Public URL Settings */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-medium mb-4">Public URL Settings</h3>
            
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={formData.allowPublicUrl || false}
                onChange={(e) => handleInputChange('allowPublicUrl', e.target.checked)}
                className="mr-2"
                id="allowPublicUrl"
              />
              <label htmlFor="allowPublicUrl" className="text-sm font-medium text-gray-700">
                Enable Public URL (Allow external submissions)
              </label>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              When enabled, a public link will be generated that allows anyone to submit data to this form without logging in.
            </p>

            {formData.allowPublicUrl && (
              <div className="space-y-4 bg-gray-50 p-4 rounded-md">
                {/* Show public URL - use existing token or temporary token for preview */}
                {(page?.publicUrlToken || formData.publicUrlToken) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <label className="block text-sm font-medium text-blue-900 mb-1">
                      Public Form URL
                      {formData.publicUrlToken && !page?.publicUrlToken && (
                        <span className="ml-2 text-xs text-blue-600 font-normal">(Preview - will be generated on save)</span>
                      )}
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={`${window.location.origin}/public-form-custom/${page?.publicUrlToken || formData.publicUrlToken}`}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white border border-blue-300 rounded-md text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const url = `${window.location.origin}/public-form-custom/${page?.publicUrlToken || formData.publicUrlToken}`;
                          navigator.clipboard.writeText(url);
                          toast.success('URL copied to clipboard!');
                        }}
                        className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Form Title
                  </label>
                  <input
                    type="text"
                    value={formData.publicUrlSettings?.customTitle || ''}
                    onChange={(e) => handleNestedChange('publicUrlSettings', 'customTitle', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Leave empty to use page name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Description
                  </label>
                  <textarea
                    value={formData.publicUrlSettings?.customDescription || ''}
                    onChange={(e) => handleNestedChange('publicUrlSettings', 'customDescription', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Instructions or description for public form users"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Success Message
                  </label>
                  <input
                    type="text"
                    value={formData.publicUrlSettings?.successMessage || 'Your submission has been received successfully!'}
                    onChange={(e) => handleNestedChange('publicUrlSettings', 'successMessage', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.publicUrlSettings?.enableCaptcha || false}
                    onChange={(e) => handleNestedChange('publicUrlSettings', 'enableCaptcha', e.target.checked)}
                    className="mr-2"
                    id="enableCaptcha"
                  />
                  <label htmlFor="enableCaptcha" className="text-sm font-medium text-gray-700">
                    Enable CAPTCHA Verification
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.publicUrlSettings?.allowPublicSubmissionAtGroupLevel !== false}
                    onChange={(e) => handleNestedChange('publicUrlSettings', 'allowPublicSubmissionAtGroupLevel', e.target.checked)}
                    className="mr-2"
                    id="allowPublicSubmissionAtGroupLevel"
                  />
                  <label htmlFor="allowPublicSubmissionAtGroupLevel" className="text-sm font-medium text-gray-700">
                    Allow Public Submissions at Group Level
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Task Settings */}
          {formData.formSubmissionAction === 'create_task' && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-medium mb-4">Task Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Task Name Field
                  </label>
                  <select
                    value={formData.taskSettings.taskNameField}
                    onChange={(e) => handleNestedChange('taskSettings', 'taskNameField', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a field</option>
                    {formData.fields.map((field, index) => (
                      <option key={index} value={field.name}>{field.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Task Description Field
                  </label>
                  <select
                    value={formData.taskSettings.taskDescriptionField}
                    onChange={(e) => handleNestedChange('taskSettings', 'taskDescriptionField', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a field</option>
                    {formData.fields.map((field, index) => (
                      <option key={index} value={field.name}>{field.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Deadline Field
                  </label>
                  <select
                    value={formData.taskSettings.deadlineField}
                    onChange={(e) => handleNestedChange('taskSettings', 'deadlineField', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a field</option>
                    {formData.fields.filter(f => f.type === 'date').map((field, index) => (
                      <option key={index} value={field.name}>{field.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Deadline (Days)
                  </label>
                  <input
                    type="number"
                    value={formData.taskSettings.defaultDeadline.days}
                    onChange={(e) => handleNestedChange('taskSettings', 'defaultDeadline', {
                      ...formData.taskSettings.defaultDeadline,
                      days: parseInt(e.target.value) || 0
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Invoice Settings */}
          {formData.formSubmissionAction === 'create_invoice' && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-medium mb-4">Invoice Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.invoiceSettings.enabled}
                    onChange={(e) => handleNestedChange('invoiceSettings', 'enabled', e.target.checked)}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Enable Invoice Creation
                  </label>
                </div>
                {formData.invoiceSettings.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fixed Value
                      </label>
                      <input
                        type="number"
                        value={formData.invoiceSettings.fixedValue}
                        onChange={(e) => handleNestedChange('invoiceSettings', 'fixedValue', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={formData.invoiceSettings.description}
                        onChange={(e) => handleNestedChange('invoiceSettings', 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Invoice description"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Fields */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Form Fields</h3>
              <button
                type="button"
                onClick={addField}
                className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 transition-colors"
              >
                Add Field
              </button>
            </div>
            
            <div className="space-y-4">
              {formData.fields.map((field, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="font-medium">Field {index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeField(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Field Name *
                      </label>
                      <input
                        type="text"
                        value={field.name}
                        onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., client_name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Field Label *
                      </label>
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Client Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Field Type *
                      </label>
                      <select
                        value={field.type}
                        onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {fieldTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Placeholder
                      </label>
                      <input
                        type="text"
                        value={field.placeholder}
                        onChange={(e) => handleFieldChange(index, 'placeholder', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter placeholder text"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => handleFieldChange(index, 'required', e.target.checked)}
                        className="mr-2"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Required Field
                      </label>
                    </div>
                    
                    {field.type === 'date' && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={field.isDeadlineField}
                          onChange={(e) => handleFieldChange(index, 'isDeadlineField', e.target.checked)}
                          className="mr-2"
                        />
                        <label className="text-sm font-medium text-gray-700">
                          Use as Deadline Field
                        </label>
                      </div>
                    )}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.showInTable}
                        onChange={(e) => handleFieldChange(index, 'showInTable', e.target.checked)}
                        className="mr-2"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Show in Table
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.showFilter !== undefined ? field.showFilter : true}
                        onChange={(e) => handleFieldChange(index, 'showFilter', e.target.checked)}
                        className="mr-2"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Show Filter as Dropdown
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.autoFill || false}
                        onChange={(e) => handleFieldChange(index, 'autoFill', e.target.checked)}
                        className="mr-2"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Auto Fill
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.consignmentTracking || false}
                        onChange={(e) => handleFieldChange(index, 'consignmentTracking', e.target.checked)}
                        className="mr-2"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Consignment Tracking
                      </label>
                    </div>
                  </div>
                  
                  {/* Dropdown Options */}
                  {field.type === 'dropdown' && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dropdown Options
                      </label>
                      <div className="space-y-2">
                        {(field.options || []).map((option, optionIndex) => (
                          <div key={optionIndex} className="flex gap-2">
                            <input
                              type="text"
                              value={option}
                              onChange={(e) => handleOptionChange(index, optionIndex, e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Option text"
                            />
                            <button
                              type="button"
                              onClick={() => removeOption(index, optionIndex)}
                              className="text-red-600 hover:text-red-800 px-2"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addOption(index)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          + Add Option
                        </button>
                      </div>
                    </div>
                  )}

                  {/* File Upload Configuration */}
                  {field.type === 'upload' && (
                    <div className="mt-4 border-t pt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Allowed File Types
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        Select which file types users can upload. Users will also be able to paste files (Ctrl+V).
                      </p>
                      
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                        {[
                          { value: 'application/pdf', label: 'PDF Documents', ext: '.pdf' },
                          { value: 'image/jpeg,image/jpg', label: 'JPEG Images', ext: '.jpg,.jpeg' },
                          { value: 'image/png', label: 'PNG Images', ext: '.png' },
                          { value: 'image/*', label: 'All Images', ext: '.jpg,.jpeg,.png,.gif,.webp' },
                          { value: 'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document', label: 'Word Documents', ext: '.doc,.docx' },
                          { value: 'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', label: 'Excel Files', ext: '.xls,.xlsx' },
                          { value: 'text/csv', label: 'CSV Files', ext: '.csv' },
                          { value: 'application/zip,application/x-zip-compressed', label: 'ZIP Archives', ext: '.zip' }
                        ].map((fileType) => {
                          // Ensure acceptedFileTypes is always an array
                          const currentTypes = normalizeAcceptedFileTypes(field.acceptedFileTypes);
                          const fileTypeExtensions = fileType.ext.split(',').map(e => e.trim());
                          // Checkbox is checked if ALL its extensions are in the saved array
                          const isChecked = fileTypeExtensions.length > 0 && fileTypeExtensions.every(ext => currentTypes.includes(ext));
                          
                          return (
                            <div key={fileType.value} className="flex items-start">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  const currentTypesArray = normalizeAcceptedFileTypes(field.acceptedFileTypes);
                                  
                                  let newTypes;
                                  if (e.target.checked) {
                                    // Add extensions that aren't already in the list (prevent duplicates)
                                    newTypes = [...currentTypesArray, ...fileTypeExtensions.filter(ext => !currentTypesArray.includes(ext))];
                                  } else {
                                    // Remove all extensions for this file type
                                    newTypes = currentTypesArray.filter(t => !fileTypeExtensions.includes(t));
                                  }
                                  
                                  // Remove any duplicates and ensure it's an array
                                  const uniqueTypes = [...new Set(newTypes)];
                                  handleFieldChange(index, 'acceptedFileTypes', uniqueTypes);
                                }}
                                className="mr-2 mt-1"
                                id={`filetype-${index}-${fileType.value}`}
                              />
                              <label 
                                htmlFor={`filetype-${index}-${fileType.value}`}
                                className="text-sm text-gray-700 cursor-pointer"
                              >
                                {fileType.label}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                      
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Maximum File Size (MB)
                        </label>
                        <input
                          type="number"
                          value={field.maxFileSize || 10}
                          onChange={(e) => handleFieldChange(index, 'maxFileSize', parseInt(e.target.value) || 10)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          min="1"
                          max="100"
                          placeholder="10"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Maximum size for each uploaded file (1-100 MB)
                        </p>
                      </div>

                      {(field.acceptedFileTypes || []).length > 0 && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md">
                          <p className="text-xs font-medium text-gray-700 mb-1">Preview:</p>
                          <p className="text-xs text-gray-600">
                            Accept: <code className="bg-white px-1 py-0.5 rounded">{(field.acceptedFileTypes || []).join(', ')}</code>
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            Max size: <strong>{field.maxFileSize || 10} MB</strong>
                          </p>
                        </div>
                      )}
                      
                      {(!field.acceptedFileTypes || field.acceptedFileTypes.length === 0) && (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                          <p className="text-xs text-yellow-800">
                            ⚠️ No file types selected. All file types will be allowed (not recommended for security).
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Verification Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verification Types
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {verificationTypes.map((vt) => (
                <div key={vt._id} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.verificationTypes.includes(vt._id)}
                    onChange={(e) => {
                      const newTypes = e.target.checked
                        ? [...formData.verificationTypes, vt._id]
                        : formData.verificationTypes.filter(id => id !== vt._id);
                      handleInputChange('verificationTypes', newTypes);
                    }}
                    className="mr-2"
                  />
                  <label className="text-sm text-gray-700">{vt.name}</label>
                </div>
              ))}
            </div>
          </div>

          {/* Checklist */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Checklist</label>
              {formData.documentVerifications.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearAllDocuments}
                  className="text-xs text-red-600 hover:text-red-800"
                >
                  Clear All
                </button>
              )}
            </div>
            
            {/* Selected Documents Tags */}
            {formData.documentVerifications.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {formData.documentVerifications.map(documentId => {
                  // documentId is already a string (ID) after normalization, not an object
                  const document = documentVerifications.find(d => d._id === documentId);
                  return document ? (
                    <span
                      key={documentId}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
                    >
                      {document.name}
                      <button
                        type="button"
                        onClick={() => handleRemoveDocument(documentId)}
                        className="ml-2 text-purple-600 hover:text-purple-800"
                      >
                        ✕
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            )}

            {/* Search and Add Documents */}
            <div className="relative document-dropdown">
              <input
                type="text"
                placeholder="Search documents to add..."
                value={documentSearchTerm}
                onChange={(e) => {
                  setDocumentSearchTerm(e.target.value);
                  setShowDocumentDropdown(true);
                }}
                onFocus={() => setShowDocumentDropdown(true)}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
              
              {/* Document Dropdown */}
              {showDocumentDropdown && documentSearchTerm && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {filteredDocuments.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500">
                      No documents found matching "{documentSearchTerm}"
                    </div>
                  ) : (
                    <div className="py-1">
                      {filteredDocuments.map((document) => (
                        <button
                          key={document._id}
                          onClick={() => handleAddDocument(document)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 text-sm"
                        >
                          <div className="font-medium text-gray-900">
                            {document.name}
                          </div>
                          {document.description && (
                            <div className="text-gray-500">{document.description}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Access Control */}
          <div>
            <h3 className="text-lg font-medium mb-4">Access Control</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Users</label>
                  {formData.accessUsers.length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearAllUsers}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                
                {/* Selected Users Tags */}
                {formData.accessUsers.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {formData.accessUsers.map(userId => {
                      const user = users.find(u => u._id === userId);
                      return user ? (
                        <span
                          key={userId}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                        >
                          {user.firstName} {user.lastName}
                          <button
                            type="button"
                            onClick={() => handleRemoveUser(userId)}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            ✕
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}

                {/* Search and Add Users */}
                <div className="relative user-dropdown">
                  <input
                    type="text"
                    placeholder="Search users to add..."
                    value={userSearchTerm}
                    onChange={(e) => {
                      setUserSearchTerm(e.target.value);
                      setShowUserDropdown(true);
                    }}
                    onFocus={() => setShowUserDropdown(true)}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                  
                  {/* User Dropdown */}
                  {showUserDropdown && userSearchTerm && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredUsers.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500">
                          No users found matching "{userSearchTerm}"
                        </div>
                      ) : (
                        <div className="py-1">
                          {filteredUsers.map((user) => (
                            <button
                              key={user._id}
                              onClick={() => handleAddUser(user._id)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 text-sm"
                            >
                              <div className="font-medium text-gray-900">
                                {user.firstName} {user.lastName}
                              </div>
                              <div className="text-gray-500">{user.email}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Groups</label>
                  {formData.accessGroups.length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearAllGroups}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                
                {/* Selected Groups Tags */}
                {formData.accessGroups.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {formData.accessGroups.map(groupId => {
                      const group = groups.find(g => g._id === groupId);
                      return group ? (
                        <span
                          key={groupId}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                        >
                          {group.name}
                          <button
                            type="button"
                            onClick={() => handleRemoveGroup(groupId)}
                            className="ml-2 text-green-600 hover:text-green-800"
                          >
                            ✕
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}

                {/* Search and Add Groups */}
                <div className="relative group-dropdown">
                  <input
                    type="text"
                    placeholder="Search groups to add..."
                    value={groupSearchTerm}
                    onChange={(e) => {
                      setGroupSearchTerm(e.target.value);
                      setShowGroupDropdown(true);
                    }}
                    onFocus={() => setShowGroupDropdown(true)}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                  
                  {/* Group Dropdown */}
                  {showGroupDropdown && groupSearchTerm && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredGroups.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500">
                          No groups found matching "{groupSearchTerm}"
                        </div>
                      ) : (
                        <div className="py-1">
                          {filteredGroups.map((group) => (
                            <button
                              key={group._id}
                              onClick={() => handleAddGroup(group._id)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 text-sm"
                            >
                              <div className="font-medium text-gray-900">
                                {group.name}
                              </div>
                              {group.description && (
                                <div className="text-gray-500">{group.description}</div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Document Generation Settings */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Document Generation Settings</h3>
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => {
                    const newDoc = {
                      documentName: '',
                      templatePath: '',
                      generationCondition: {
                        type: 'on_submission',
                        statusValue: '',
                        fieldName: ''
                      },
                      enabled: true,
                      order: (prev.documentGenerationSettings || []).length
                    };
                    const newSettings = [
                      ...(prev.documentGenerationSettings || []),
                      newDoc
                    ];
                    console.log('Added new document generation setting. Total count:', newSettings.length);
                    return {
                      ...prev,
                      documentGenerationSettings: newSettings
                    };
                  });
                }}
                className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 transition-colors text-sm"
              >
                Add Document
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Configure documents to be automatically generated when specific conditions are met.
              Documents use [[%%FIELDNAME%%]] tokens that will be replaced with actual field values.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-yellow-800">
                <strong>Note:</strong> Both Document Name and Template File are required. Incomplete document settings will not be saved.
                For "On Status Change" conditions, you must select a status. For "On Field Change" conditions, you must select a field.
              </p>
            </div>

            {!formData.documentGenerationSettings || formData.documentGenerationSettings.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No document generation configured</p>
            ) : (
              <div className="space-y-4">
                {formData.documentGenerationSettings.map((doc, index) => (
                  <div key={index} className={`border rounded-lg p-4 ${
                    doc.documentName && doc.templatePath ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-gray-900">Document {index + 1}</h4>
                        {doc.documentName && doc.templatePath ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">Complete</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">Incomplete</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            documentGenerationSettings: (prev.documentGenerationSettings || []).filter((_, i) => i !== index)
                          }));
                        }}
                        className="text-red-600 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="space-y-3">
                      {/* Document Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Document Name *
                        </label>
                        <input
                          type="text"
                          value={doc.documentName}
                          onChange={(e) => {
                            const newDocs = [...(formData.documentGenerationSettings || [])];
                            newDocs[index].documentName = e.target.value;
                            console.log(`Document ${index + 1} name updated to:`, e.target.value);
                            setFormData(prev => ({ ...prev, documentGenerationSettings: newDocs }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Agreement Letter"
                        />
                      </div>

                      {/* Template File Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Template File (DOCX) *
                        </label>
                        <input
                          type="file"
                          accept=".docx"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              console.log('Uploading template file:', file.name, 'Size:', file.size, 'bytes');
                              
                              // Validate file type
                              if (!file.name.toLowerCase().endsWith('.docx')) {
                                toast.error('Only .docx files are allowed');
                                return;
                              }
                              
                              try {
                                toast.info('Uploading template...');
                                const formDataUpload = new FormData();
                                formDataUpload.append('file', file);
                                
                                const response = await api.post('/upload', formDataUpload, {
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                
                                console.log('Upload response:', response.data);
                                
                                if (response.data.success) {
                                  const newDocs = [...(formData.documentGenerationSettings || [])];
                                  // API returns 'url' field with full URL
                                  let uploadedPath = response.data.url || response.data.filePath || response.data.path;
                                  
                                  // Convert URL to relative path if it's a full URL
                                  if (uploadedPath && uploadedPath.startsWith('http')) {
                                    // Extract path from URL: http://localhost:5000/uploads/file.docx -> uploads/file.docx
                                    const urlObj = new URL(uploadedPath);
                                    uploadedPath = urlObj.pathname.substring(1); // Remove leading /
                                  }
                                  
                                  newDocs[index].templatePath = uploadedPath;
                                  setFormData(prev => ({ ...prev, documentGenerationSettings: newDocs }));
                                  console.log('Template path set to:', uploadedPath);
                                  console.log('Full document setting:', newDocs[index]);
                                  toast.success('Template uploaded successfully');
                                } else {
                                  toast.error(response.data.message || 'Upload failed');
                                }
                              } catch (error) {
                                console.error('Error uploading template:', error);
                                console.error('Error details:', error.response?.data);
                                toast.error(error.response?.data?.message || 'Failed to upload template');
                              }
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {doc.templatePath && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Template uploaded: {doc.templatePath.split('/').pop()}
                          </p>
                        )}
                      </div>

                      {/* Generation Condition Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Generation Condition *
                        </label>
                        <select
                          value={doc.generationCondition.type}
                          onChange={(e) => {
                            const newDocs = [...(formData.documentGenerationSettings || [])];
                            newDocs[index].generationCondition.type = e.target.value;
                            newDocs[index].generationCondition.statusValue = '';
                            newDocs[index].generationCondition.fieldName = '';
                            setFormData(prev => ({ ...prev, documentGenerationSettings: newDocs }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="on_submission">On Submission</option>
                          <option value="on_status_change">On Status Changed To</option>
                          <option value="on_field_change">On Field Value Changed</option>
                        </select>
                      </div>

                      {/* Status Value (if on_status_change) */}
                      {doc.generationCondition.type === 'on_status_change' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Status Value *
                          </label>
                          <select
                            value={doc.generationCondition.statusValue}
                            onChange={(e) => {
                              const newDocs = [...(formData.documentGenerationSettings || [])];
                              newDocs[index].generationCondition.statusValue = e.target.value;
                              setFormData(prev => ({ ...prev, documentGenerationSettings: newDocs }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select status...</option>
                            {formData.customStatuses.map((status, i) => (
                              <option key={i} value={status}>{status}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Field Name (if on_field_change) */}
                      {doc.generationCondition.type === 'on_field_change' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Field Name *
                          </label>
                          <select
                            value={doc.generationCondition.fieldName}
                            onChange={(e) => {
                              const newDocs = [...(formData.documentGenerationSettings || [])];
                              newDocs[index].generationCondition.fieldName = e.target.value;
                              setFormData(prev => ({ ...prev, documentGenerationSettings: newDocs }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select field...</option>
                            {formData.fields.map((field, i) => (
                              <option key={i} value={field.name}>{field.label} ({field.name})</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Enabled Toggle */}
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={doc.enabled}
                          onChange={(e) => {
                            const newDocs = [...(formData.documentGenerationSettings || [])];
                            newDocs[index].enabled = e.target.checked;
                            setFormData(prev => ({ ...prev, documentGenerationSettings: newDocs }));
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-700">
                          Enabled
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Summary of what will be saved */}
            {formData.documentGenerationSettings && formData.documentGenerationSettings.length > 0 && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm font-medium text-blue-900 mb-1">Save Summary:</p>
                <p className="text-xs text-blue-800">
                  {(() => {
                    const completeCount = formData.documentGenerationSettings.filter(doc => 
                      doc.documentName && doc.documentName.trim() !== '' && 
                      doc.templatePath && doc.templatePath.trim() !== ''
                    ).length;
                    const totalCount = formData.documentGenerationSettings.length;
                    const incompleteCount = totalCount - completeCount;
                    
                    if (completeCount === 0) {
                      return `⚠️ No complete document settings to save. All ${totalCount} setting(s) are incomplete.`;
                    } else if (incompleteCount === 0) {
                      return `✓ ${completeCount} complete document setting(s) will be saved.`;
                    } else {
                      return `✓ ${completeCount} complete document setting(s) will be saved. ${incompleteCount} incomplete setting(s) will be ignored.`;
                    }
                  })()}
                </p>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Saving...' : (page ? 'Update Page' : 'Create Page')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomPageForm; 